import { useEffect, useState } from 'react';
import type { IServiceOrganizationSettings } from '@common/interfaces/servicesByServiceGroupForEpisodeCategory';
import { ServiceCode } from '@common/enums/serviceCode';
import { addCarePathServiceSettingsSchema } from '@common/validation/addCarePath';
import { scrollToValidationError } from '@frontend/utils';
import type { ValidationErrors } from '../../../../utils/validation';
import type { ICareProviderAddCarePathState } from '../../../interfaces/careProviderAddCarePathState';
import type { RealServiceOrganizationSettings } from '../../carePath/settings/ServiceOrganizationSettings';
import {
    emptyValidationErrors,
    errorsDeepCompareValue,
    updateValidationErrorsForOne,
} from '../../../../utils/validation';
import { ChooseCarePathStep } from '../../../interfaces/careProviderAddCarePathState';
import { ServiceOrganizationSettings } from '../../carePath/settings/ServiceOrganizationSettings';

export interface ISettingsProps {
    errors?: ValidationErrors;
    state: ICareProviderAddCarePathState;
    updateState: (state: Partial<ICareProviderAddCarePathState>, carePathStep: ChooseCarePathStep) => void;
}

export function PersonalSettingsAddCarePathServiceOrganizationSettings(props: ISettingsProps) {
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [saveErrorsCounter, setSaveErrorsCounter] = useState(0);
    const [serviceOrganizationSettings, setServiceOrganizationSettings] = useState<IServiceOrganizationSettings[]>([]);

    useEffect(() => {
        setServiceOrganizationSettingsInServiceGroups();
    }, [props.state]);

    useEffect(() => {
        setErrors(props.errors ?? emptyValidationErrors);
        setSaveErrorsCounter((counter) => counter + 1);
    }, [errorsDeepCompareValue(props.errors)]);

    useEffect(() => {
        if (errors && errors.size > 0) {
            scrollToValidationError(true);
        }
    }, [saveErrorsCounter]);

    return (
        <>
            {serviceOrganizationSettings.map((settings) => (
                <ServiceOrganizationSettings
                    errors={errors}
                    getErrorKey={getErrorKey}
                    getServiceIndex={getServiceIndex}
                    hasServiceProviderAndServiceConsumerForOsasv={isRoleOsasvConsumerAndProvider(settings)}
                    key={settings.serviceId}
                    onChange={handleChange}
                    onEmbloomQuestionnairesChange={handleEmbloomQuestionnairesChange}
                    onValidateOne={handleValidateOne}
                    organization={undefined}
                    organizationServiceProvidersSettings={{}}
                    serviceOrganizationSettings={settings}
                    serviceProviderIsRequestRecipient={getIsServiceProviderRequestRecipientValue(settings.serviceId)}
                />
            ))}
        </>
    );

    function isRoleOsasvConsumerAndProvider(settings: IServiceOrganizationSettings) {
        if (settings.service.code !== ServiceCode.OSASV) {
            return false;
        }

        const currentServiceGroup = props.state.serviceGroups?.find((serviceGroup) =>
            (serviceGroup.services ?? []).some((service) => service.id === settings.serviceId)
        );
        const isServiceConsumer = (currentServiceGroup?.profileSettings?.serviceConsumerSettings ?? []).some(
            (setting) => setting.service.code === ServiceCode.OSASV && setting.isDisabled === false
        );
        const isServiceProvider = (currentServiceGroup?.profileSettings?.serviceProviderSettings ?? []).some(
            (setting) => setting.service.code === ServiceCode.OSASV && setting.isDisabled === false
        );

        return isServiceConsumer && isServiceProvider;
    }

    function setServiceOrganizationSettingsInServiceGroups() {
        const sos: IServiceOrganizationSettings[] = [];
        props.state.serviceGroups?.forEach((serviceGroup) => {
            if (serviceGroup.serviceOrganizationSettings) {
                serviceGroup.serviceOrganizationSettings.forEach((settings) => {
                    sos.push({ ...settings });
                });
            }
        });
        setServiceOrganizationSettings(sos);
    }

    function getServiceIndex(settings: IServiceOrganizationSettings) {
        return serviceOrganizationSettings.findIndex(
            (organizationSettings) => organizationSettings.service.code === settings.service.code
        );
    }

    function getErrorKey(settings: IServiceOrganizationSettings, key: string) {
        const serviceIndex = getServiceIndex(settings);
        if (serviceIndex === undefined || serviceIndex === -1) {
            return undefined;
        }

        return `serviceOrganizationSettings.${serviceIndex}.${key}`;
    }

    function handleChange(settings: IServiceOrganizationSettings, key: any, value: any) {
        const updatedServiceOrganizationSettings = { ...settings, [key]: value };

        const serviceGroups = getServiceGroupsWithUpdatedServiceOrganizationSettings(
            updatedServiceOrganizationSettings
        );

        props.updateState({ serviceGroups }, ChooseCarePathStep.updateServiceOrganizationSettings);
    }

    function handleValidateOne(settings: IServiceOrganizationSettings, key: keyof RealServiceOrganizationSettings) {
        const errorKey = getErrorKey(settings, key);
        if (errorKey) {
            const validationErrors = updateValidationErrorsForOne(
                addCarePathServiceSettingsSchema,
                { serviceOrganizationSettings },
                errors,
                errorKey
            );
            setErrors(validationErrors);
        }
    }

    function handleEmbloomQuestionnairesChange(
        settings: IServiceOrganizationSettings,
        selectedEmbloomQuestionnaires: string[]
    ) {
        const updatedServiceOrganizationSettings = {
            ...settings,
            embloomQuestionnaires: selectedEmbloomQuestionnaires,
        };

        const serviceGroups = getServiceGroupsWithUpdatedServiceOrganizationSettings(
            updatedServiceOrganizationSettings
        );
        props.updateState({ serviceGroups }, ChooseCarePathStep.updateServiceOrganizationSettings);
    }

    function getServiceGroupsWithUpdatedServiceOrganizationSettings(updatedSettings: IServiceOrganizationSettings) {
        return props.state.serviceGroups?.map((serviceGroup) => {
            const updatedServiceGroup = { ...serviceGroup };
            updatedServiceGroup.serviceOrganizationSettings = updatedServiceGroup.serviceOrganizationSettings?.map(
                (originalSettings) => {
                    return originalSettings.serviceId === updatedSettings.serviceId
                        ? updatedSettings
                        : originalSettings;
                }
            );

            return updatedServiceGroup;
        });
    }

    function getIsServiceProviderRequestRecipientValue(serviceId: string) {
        const currentServiceGroup = props.state.serviceGroups?.find((serviceGroup) =>
            (serviceGroup.services ?? []).some((service) => service.id === serviceId)
        );
        const serviceProviderSetting = (currentServiceGroup?.profileSettings?.serviceProviderSettings ?? []).find(
            (setting) => setting.service.id === serviceId
        );

        return serviceProviderSetting && serviceProviderSetting.isRequestRecipient;
    }
}
